import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { APP_LINKS, PROBLEMS, FEATURES, HOW_IT_WORKS, SERVICE_CATEGORIES, FAQS, CUSTOMER_PLANS, VENDOR_PLANS, APP_SCREENSHOTS, STORE_LINKS, REMINDER_TYPES, MONEY_AREAS } from "../constants/site";
import { Seo } from "../components/site/Seo";
import { Reveal, SectionHead } from "../components/site/Shared";
import { Icon } from "../components/site/Icon";
import { AppMockup } from "../components/site/AppMockup";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const MARQUEE_ITEMS = [
  "Asset tracking", "Multi-property", "Smart reminders", "Money management",
  "Document vault", "Service bookings", "Family sharing", "AI scanning", "Expense insights",
];

const Marquee = () => (
  <div className="overflow-hidden border-y border-teal-deep/10 bg-white py-4" aria-hidden="true">
    <div className="marquee-track flex w-max items-center gap-10">
      {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, i) => (
        <span key={i} className="flex items-center gap-10 font-mono text-xs font-semibold uppercase tracking-[0.18em] text-teal-deep/60">
          {item}
          <span className="h-1.5 w-1.5 rounded-full bg-teal/50" />
        </span>
      ))}
    </div>
  </div>
);

const Hero = () => (
  <section className="relative overflow-hidden">
    <div
      className="pointer-events-none absolute -right-40 -top-40 h-[480px] w-[480px] rounded-full opacity-[0.08]"
      style={{ backgroundImage: "radial-gradient(circle,#148A9D,transparent 65%)" }}
    />
    <div className="container-site grid items-center gap-14 py-20 lg:grid-cols-12 lg:py-28">
      <div className="lg:col-span-7">
        <Reveal>
          <p className="eyebrow mb-5">Household management · Made for India</p>
          <h1 className="font-display text-[clamp(40px,5.5vw,76px)] font-semibold leading-[1.02] tracking-tight text-ink">
            Everything your home needs.{" "}
            <em className="not-italic text-teal-deep" style={{ fontStyle: "italic" }}>
              Aligned in one place.
            </em>
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-ink/65 md:text-lg">
            Manage your household, assets, documents, expenses and bills, stay on top of
            maintenance and connect with trusted home-service professionals — all from one place.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-4">
            <a href={APP_LINKS.getStarted} data-testid="hero-get-started-btn" className="btn-primary">
              Get Started <Icon name="ArrowRight" className="h-4 w-4" />
            </a>
            <Link to="/features" data-testid="hero-explore-btn" className="btn-secondary">
              Explore Features
            </Link>
          </div>
          <p className="mt-6 flex items-center gap-2 text-sm text-ink/50">
            <Icon name="Check" className="h-4 w-4 text-teal" />
            Designed for households across India — free plan included.
          </p>
        </Reveal>
      </div>
      <div className="relative lg:col-span-5">
        <Reveal delay={1} className="flex justify-center">
          <div className="animate-drift">
            <AppMockup variant="dashboard" />
          </div>
        </Reveal>
        <p className="mt-4 text-center text-xs text-ink/40">Illustrative preview of the HomeAligns app</p>
      </div>
    </div>
  </section>
);

const Problems = () => (
  <section className="py-24">
    <div className="container-site">
      <SectionHead
        eyebrow="The everyday mess"
        title="Running a home shouldn't feel like paperwork."
        sub="Warranty cards in drawers, service dates in your head, documents in old chats. HomeAligns brings it all together — one household, one app."
      />
      <div className="grid gap-6 md:grid-cols-2">
        {PROBLEMS.map((p, i) => (
          <Reveal key={p.title} delay={(i % 2) + 1} className="card-site p-8">
            <div className="flex items-start gap-5">
              <span className="icon-tile mt-0.5">
                <Icon name={["FileText", "BellRing", "TrendingDown", "Wrench"][i]} className="h-5 w-5" />
              </span>
              <div>
                <h3 className="text-lg font-bold text-ink line-through decoration-teal/50 decoration-2">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-ink/60">{p.text}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal className="mt-10">
        <div className="rounded-3xl p-8 text-white md:p-10" style={{ backgroundImage: "linear-gradient(125deg,#0A4D68,#148A9D)" }}>
          <p className="font-display text-2xl font-semibold leading-snug md:text-3xl">
            HomeAligns turns that scattered admin into a single, organised system for your home.
          </p>
          <Link to="/features" data-testid="problem-solution-btn" className="mt-6 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-bold text-teal-deep transition-transform duration-300 hover:-translate-y-0.5">
            See how it works <Icon name="ArrowRight" className="h-4 w-4" />
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);

const FeaturesGrid = () => (
  <section className="bg-white py-24">
    <div className="container-site">
      <SectionHead
        eyebrow="One platform, every household need"
        title="Everything your home admin, finally aligned."
        sub="Assets, documents, warranties, reminders, money and services — live in the HomeAligns app today, unless marked coming soon."
      />
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {FEATURES.map((f, i) => (
          <Reveal key={f.title} delay={(i % 3) + 1} className="card-site group p-7">
            <span className="icon-tile transition-transform duration-300 group-hover:-translate-y-1">
              <Icon name={f.icon} className="h-5 w-5" />
            </span>
            <h3 className="mt-5 text-base font-bold text-ink">{f.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-ink/60">{f.text}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

// Phone frame used for real app screenshots.
const ShotFrame = ({ src, alt, className = "" }) => (
  <div
    className={`relative w-[290px] shrink-0 overflow-hidden rounded-[2.6rem] border-[6px] border-ink/90 bg-white shadow-2xl ${className}`}
  >
    <div className="pointer-events-none absolute left-1/2 top-2.5 z-10 h-5 w-24 -translate-x-1/2 rounded-full bg-ink/90" />
    <img src={src} alt={alt} loading="lazy" className="block w-full" />
  </div>
);

const SmartReminders = () => (
  <section className="py-24">
    <div className="container-site">
      <div className="grid items-center gap-12 lg:grid-cols-2">
        <Reveal>
          <p className="eyebrow mb-4">Smart reminders</p>
          <h2 className="font-display text-[clamp(30px,4vw,46px)] font-semibold leading-[1.08] tracking-tight text-ink">
            Never miss a due date again.
          </h2>
          <p className="mt-5 text-base leading-relaxed text-ink/65">
            HomeAligns reminds you <strong className="text-ink">10 days before</strong>,{" "}
            <strong className="text-ink">5 days before</strong> and{" "}
            <strong className="text-ink">on the due date</strong> — in the app and by email — so
            servicing, renewals and payments never slip through.
          </p>
          <div className="mt-7 flex flex-wrap gap-2.5">
            {REMINDER_TYPES.map((t) => (
              <span key={t} className="rounded-full border border-teal-deep/15 bg-white px-4 py-2 text-xs font-bold text-teal-deep">
                {t}
              </span>
            ))}
          </div>
          <p className="mt-6 text-xs leading-relaxed text-ink/45">
            Reminders arrive in-app and by email. Overdue-payment alerts are added as they go live in the app.
          </p>
        </Reveal>
        <Reveal delay={1} className="grid gap-4">
          {[
            ["BellRing", "10 days before", "Plenty of time to book a service or arrange a payment."],
            ["CalendarCheck", "5 days before", "A second nudge while there's still time to act."],
            ["Check", "On the due date", "A final reminder on the day itself."],
          ].map(([icon, t, d], i) => (
            <div key={t} className={`card-site flex items-start gap-5 p-6 ${i === 1 ? "lg:ml-10" : ""}`}>
              <span className="icon-tile shrink-0">
                <Icon name={icon} className="h-5 w-5" />
              </span>
              <div>
                <h3 className="text-base font-bold text-ink">{t}</h3>
                <p className="mt-1 text-sm leading-relaxed text-ink/60">{d}</p>
              </div>
            </div>
          ))}
        </Reveal>
      </div>
    </div>
  </section>
);

const MoneySection = () => (
  <section className="bg-teal-deep py-24 text-white">
    <div className="texture-grid">
      <div className="container-site">
        <SectionHead
          dark
          eyebrow="Money management"
          title="Rent, bills, expenses and budgets — in every plan."
          sub="The Money tab is included for every HomeAligns customer, including the Free plan. Your membership simply sets individual feature access and usage limits."
        />
        <div className="grid gap-6 md:grid-cols-2">
          {MONEY_AREAS.map((area, i) => (
            <Reveal key={area.title} delay={(i % 2) + 1} className="rounded-3xl border border-white/15 bg-white/5 p-8 backdrop-blur-sm">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10 text-teal-soft">
                <Icon name={area.icon} className="h-5 w-5" />
              </span>
              <h3 className="mt-5 text-lg font-bold">{area.title}</h3>
              <ul className="mt-4 space-y-2.5">
                {area.points.map((p) => (
                  <li key={p} className="flex items-start gap-2.5 text-sm leading-relaxed text-white/75">
                    <Icon name="Check" className="mt-0.5 h-4 w-4 shrink-0 text-teal-soft" />
                    {p}
                  </li>
                ))}
              </ul>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-10 flex flex-wrap items-center gap-4">
          <Link to="/pricing" data-testid="money-plans-btn" className="inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-bold text-teal-deep shadow-lg transition-transform duration-300 hover:-translate-y-0.5">
            Compare plans <Icon name="ArrowRight" className="h-4 w-4" />
          </Link>
          <p className="max-w-md text-xs leading-relaxed text-white/55">
            HomeAligns tracks bills and rent — it does not process utility bill payments or rent
            collection.
          </p>
        </Reveal>
      </div>
    </div>
  </section>
);

const SavingsGoals = () => (
  <section className="bg-white py-24">
    <div className="container-site">
      <div className="grid items-center gap-12 lg:grid-cols-2">
        <Reveal>
          <div className="flex flex-wrap items-center gap-3">
            <p className="eyebrow">Savings Goals</p>
            <span className="rounded-full bg-teal-deep px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-white">Pro exclusive</span>
            <span className="rounded-full bg-amber-100 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-amber-700" data-testid="savings-coming-soon-badge">Coming soon</span>
          </div>
          <h2 className="mt-4 font-display text-[clamp(30px,4vw,46px)] font-semibold leading-[1.08] tracking-tight text-ink">
            Save for what your home needs next.
          </h2>
          <p className="mt-5 text-base leading-relaxed text-ink/65">
            Pro members will be able to set individual savings targets with a target amount and
            date, record contributions, and watch goal-wise progress alongside their household
            expense reports. Free and Plus members will see an option to upgrade.
          </p>
          <ul className="mt-7 space-y-3.5">
            {[
              "Progress is based only on contributions you record or confirm — never on leftover-budget guesses",
              "Goal-wise progress indicators, next to your monthly expense reports",
              "Exclusive to Pro — not included in Free or Plus",
            ].map((t) => (
              <li key={t} className="flex items-start gap-3 text-sm font-medium text-ink/75">
                <Icon name="Check" className="mt-0.5 h-4 w-4 shrink-0 text-teal" />
                {t}
              </li>
            ))}
          </ul>
        </Reveal>
        <Reveal delay={1} className="flex justify-center">
          <div className="card-site w-full max-w-md p-8">
            <div className="flex items-center gap-3">
              <span className="icon-tile"><Icon name="PiggyBank" className="h-5 w-5" /></span>
              <p className="text-sm font-bold text-ink">New appliances fund</p>
              <span className="ml-auto rounded-full bg-amber-100 px-2.5 py-0.5 text-[10px] font-bold uppercase text-amber-700">Preview</span>
            </div>
            <div className="mt-6 space-y-5">
              {[
                ["Washing machine upgrade", 62],
                ["Diwali renovation", 35],
                ["Emergency fund", 80],
              ].map(([goal, pct]) => (
                <div key={goal}>
                  <div className="flex items-center justify-between text-xs font-semibold text-ink/60">
                    <span>{goal}</span>
                    <span className="text-teal-deep">{pct}%</span>
                  </div>
                  <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-mist">
                    <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundImage: "linear-gradient(90deg,#0A4D68,#148A9D)" }} />
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-5 text-[11px] leading-relaxed text-ink/45">
              Illustrative preview. Progress counts only contributions recorded by you.
            </p>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const ProductDemo = () => {
  const hasShots = APP_SCREENSHOTS.length > 0;
  return (
    <section className="overflow-hidden bg-mist/60 py-24">
      <div className="container-site">
        <SectionHead
          align="center"
          eyebrow="Inside the app"
          title="A calm, organised home screen for your whole household."
          sub={
            hasShots
              ? "Real screens from the HomeAligns app — dashboards for assets and value tracking, private document storage, warranty reminders and service bookings."
              : "Dashboards for assets and value tracking, private document storage, warranty reminders and service bookings — illustrated from the real HomeAligns interface."
          }
        />
        {hasShots ? (
          <div className="flex flex-wrap items-start justify-center gap-8">
            {APP_SCREENSHOTS.slice(0, 3).map((s, i) => (
              <Reveal key={s.src} delay={i + 1} className={i === 1 ? "lg:mt-14" : ""}>
                <figure>
                  <ShotFrame src={s.src} alt={s.alt} />
                  <figcaption className="mt-3 text-center text-xs font-semibold text-ink/55">{s.label}</figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        ) : (
          <div className="flex flex-wrap items-start justify-center gap-8">
            <Reveal delay={1}><AppMockup variant="asset" /></Reveal>
            <Reveal delay={2} className="lg:mt-14"><AppMockup variant="dashboard" /></Reveal>
            <Reveal delay={3}><AppMockup variant="services" /></Reveal>
          </div>
        )}
        {!hasShots && (
          <p className="mt-8 text-center text-xs text-ink/40">Illustrative previews — sample data, not real user records.</p>
        )}
      </div>
    </section>
  );
};

const HowItWorks = () => (
  <section className="py-24">
    <div className="container-site">
      <SectionHead
        eyebrow="How it works"
        title="Set up in three simple steps."
      />
      <div className="grid gap-6 md:grid-cols-3">
        {HOW_IT_WORKS.map((s, i) => (
          <Reveal key={s.step} delay={i + 1} className="card-site relative overflow-hidden p-8">
            <span className="font-display text-6xl font-semibold text-teal-mist">{s.step}</span>
            <h3 className="mt-4 text-lg font-bold text-ink">{s.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-ink/60">{s.text}</p>
          </Reveal>
        ))}
      </div>
      <Reveal className="mt-10">
        <a href={APP_LINKS.registerCustomer} data-testid="hiw-create-account-btn" className="btn-primary">
          Create your household account <Icon name="ArrowRight" className="h-4 w-4" />
        </a>
      </Reveal>
    </div>
  </section>
);

const IndiaSection = () => (
  <section className="bg-white py-24">
    <div className="container-site grid items-center gap-12 lg:grid-cols-2">
      <Reveal>
        <p className="eyebrow mb-4">Designed for Indian households</p>
        <h2 className="font-display text-[clamp(30px,4vw,46px)] font-semibold leading-[1.08] tracking-tight text-ink">
          Built around how Indian homes actually run.
        </h2>
        <p className="mt-5 text-base leading-relaxed text-ink/65">
          Multiple properties across cities, documents that matter, family members who share the
          load, and the service categories Indian homes use most — HomeAligns is designed around
          all of it, with prices in rupees and support at home.
        </p>
        <ul className="mt-7 space-y-3.5">
          {[
            "Track your flat, your family home and rental properties side by side",
            "Keep property, tax and society documents alongside every asset",
            "Share access with family members at the permission level you choose",
            "Indian service categories — from RO and geyser service to pest control",
          ].map((t) => (
            <li key={t} className="flex items-start gap-3 text-sm font-medium text-ink/75">
              <Icon name="Check" className="mt-0.5 h-4 w-4 shrink-0 text-teal" />
              {t}
            </li>
          ))}
        </ul>
      </Reveal>
      <Reveal delay={1} className="flex justify-center">
        <AppMockup variant="warranty" />
      </Reveal>
    </div>
  </section>
);

const ServicesPreview = () => (
  <section className="py-24">
    <div className="container-site">
      <SectionHead
        eyebrow="Home services"
        title="The pros your home needs, in one catalogue."
        sub="Book approved service professionals across the categories Indian households use most. Service availability depends on approved providers in your city."
      />
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {SERVICE_CATEGORIES.map((c, i) => (
          <Reveal key={c.name} delay={(i % 4) + 1} className="card-site flex items-center gap-3.5 p-5">
            <span className="icon-tile !h-10 !w-10 !rounded-xl">
              <Icon name={c.icon} className="h-4 w-4" />
            </span>
            <span className="text-sm font-bold text-ink">{c.name}</span>
          </Reveal>
        ))}
      </div>
      <Reveal className="mt-10">
        <Link to="/home-services" data-testid="services-explore-btn" className="btn-secondary">
          Explore Home Services <Icon name="ArrowRight" className="h-4 w-4" />
        </Link>
      </Reveal>
    </div>
  </section>
);

const AISection = () => (
  <section className="bg-white py-24">
    <div className="container-site grid items-center gap-12 lg:grid-cols-2">
      <Reveal className="order-2 flex justify-center lg:order-1">
        <div className="card-site w-full max-w-md p-8">
          <div className="flex items-center gap-3">
            <span className="icon-tile"><Icon name="ScanLine" className="h-5 w-5" /></span>
            <p className="text-sm font-bold text-ink">Scan with AI</p>
          </div>
          <div className="mt-5 space-y-2.5 rounded-2xl bg-mist/70 p-5">
            {[
              ["Brand", "Read from invoice"],
              ["Model & serial", "Filled for review"],
              ["Purchase date", "Detected"],
              ["Estimated value", "Indicative only"],
            ].map(([k, v]) => (
              <div key={k} className="flex items-center justify-between text-sm">
                <span className="font-semibold text-ink/60">{k}</span>
                <span className="rounded-full bg-teal-mist px-2.5 py-0.5 text-xs font-bold text-teal-deep">{v}</span>
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs leading-relaxed text-ink/45">
            AI can make mistakes — you review every detail before anything is saved.
          </p>
        </div>
      </Reveal>
      <Reveal delay={1} className="order-1 lg:order-2">
        <p className="eyebrow mb-4">AI-powered convenience</p>
        <h2 className="font-display text-[clamp(30px,4vw,46px)] font-semibold leading-[1.08] tracking-tight text-ink">
          Less typing. More living.
        </h2>
        <p className="mt-5 text-base leading-relaxed text-ink/65">
          Scan an invoice or a product photo and AI reads the brand, model and dates for you to
          confirm. Ask the HomeAligns Assistant for help with your household. Estimated market
          values are clearly marked as estimates — helpful guidance, never presented as verified
          prices.
        </p>
        <ul className="mt-7 space-y-3.5">
          {[
            "AI scanning is optional and runs only with your consent",
            "You review and approve every AI-filled detail",
            "Manufacturer-recommended service intervals are always shown separately from AI estimates",
            "Estimates are labelled as estimates, not valuations",
          ].map((t) => (
            <li key={t} className="flex items-start gap-3 text-sm font-medium text-ink/75">
              <Icon name="Check" className="mt-0.5 h-4 w-4 shrink-0 text-teal" />
              {t}
            </li>
          ))}
        </ul>
      </Reveal>
    </div>
  </section>
);

const VendorSection = () => (
  <section className="bg-teal-deep py-24 text-white">
    <div className="texture-grid">
      <div className="container-site grid items-center gap-12 lg:grid-cols-2">
        <Reveal>
          <p className="eyebrow mb-4 !text-teal-soft">For service professionals</p>
          <h2 className="font-display text-[clamp(30px,4vw,46px)] font-semibold leading-[1.08] tracking-tight">
            Grow your business with HomeAligns.
          </h2>
          <p className="mt-5 text-base leading-relaxed text-white/75">
            Register as a Service Pro, complete verification, and build your presence on the
            platform. Receive job requests near you, manage bookings and your schedule, and get
            fast online settlements — with no commission deducted from your bookings.
          </p>
          <ul className="mt-7 space-y-3.5">
            {[
              "Visible to customers once approved and subscribed",
              "Subscriptions buy platform access — no lead or income guarantees",
              "HomeAligns never takes a commission from your bookings",
            ].map((t) => (
              <li key={t} className="flex items-start gap-3 text-sm font-medium text-white/85">
                <Icon name="BadgeCheck" className="mt-0.5 h-4 w-4 shrink-0 text-teal-soft" />
                {t}
              </li>
            ))}
          </ul>
          <div className="mt-9 flex flex-wrap gap-4">
            <a
              href={APP_LINKS.registerVendor}
              data-testid="vendor-join-btn"
              className="inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-bold text-teal-deep shadow-lg transition-transform duration-300 hover:-translate-y-0.5"
            >
              Join as a Service Professional <Icon name="ArrowRight" className="h-4 w-4" />
            </a>
            <Link
              to="/for-service-professionals"
              data-testid="vendor-learn-more-btn"
              className="inline-flex items-center gap-2 rounded-full border border-white/30 px-7 py-3.5 text-sm font-bold text-white transition-colors duration-300 hover:bg-white/10"
            >
              How it works
            </Link>
          </div>
        </Reveal>
        <Reveal delay={1} className="grid gap-4 sm:grid-cols-2">
          {[
            ["KeyRound", "Register & verify", "Create your Service Pro account and complete business verification (KYC)."],
            ["Wrench", "List your services", "Set the categories and cities you serve, and keep your profile accurate."],
            ["BellRing", "Receive requests", "See job requests from households near you and respond honestly."],
            ["IndianRupee", "Get settled online", "Manage bookings and your schedule with fast online settlements."],
          ].map(([icon, t, d], i) => (
            <div key={t} className={`rounded-3xl border border-white/15 bg-white/5 p-6 backdrop-blur-sm ${i % 2 ? "sm:mt-8" : ""}`}>
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10 text-teal-soft">
                <Icon name={icon} className="h-5 w-5" />
              </span>
              <h3 className="mt-4 text-sm font-bold">{t}</h3>
              <p className="mt-1.5 text-xs leading-relaxed text-white/65">{d}</p>
            </div>
          ))}
        </Reveal>
      </div>
    </div>
  </section>
);

const PricingPreview = () => (
  <section className="py-24">
    <div className="container-site">
      <SectionHead
        align="center"
        eyebrow="Membership & pricing"
        title="Simple plans, in rupees, no surprises."
        sub="Payments currently run in Test Mode in the app — no real money is charged. View the latest plan benefits in the app before you choose."
      />
      <div className="grid gap-6 md:grid-cols-3">
        {CUSTOMER_PLANS.map((p, i) => (
          <Reveal key={p.name} delay={i + 1} className={`card-site p-8 ${p.highlight ? "ring-2 ring-teal" : ""}`}>
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.18em] text-teal">{p.name}</p>
            <p className="mt-4 flex items-baseline gap-2">
              <span className="font-display text-4xl font-semibold text-ink">{p.price}</span>
              <span className="text-sm text-ink/55">{p.period}</span>
            </p>
            <p className="mt-3 text-sm text-ink/60">{p.blurb}</p>
          </Reveal>
        ))}
      </div>
      <Reveal className="mt-8 rounded-3xl border border-teal-deep/10 bg-white p-6 text-center">
        <p className="text-sm text-ink/65">
          The <span className="font-bold text-ink">Money tab is included in every plan, including Free</span> — your
          membership sets individual feature access and limits. Savings Goals are{" "}
          <span className="font-bold text-ink">exclusive to Pro</span>. Service professionals:{" "}
          <span className="font-bold text-ink">Starter ₹4,999 for 6 months</span> ·{" "}
          <span className="font-bold text-ink">Pro ₹8,999 per year</span> for new purchases.{" "}
          <Link to="/pricing" data-testid="pricing-preview-link" className="font-bold text-teal-deep underline underline-offset-4 hover:text-teal">
            See full pricing
          </Link>
        </p>
      </Reveal>
    </div>
  </section>
);

const TrustSection = () => (
  <section className="bg-white py-24">
    <div className="container-site grid items-center gap-12 lg:grid-cols-2">
      <Reveal>
        <p className="eyebrow mb-4">Privacy & trust</p>
        <h2 className="font-display text-[clamp(30px,4vw,46px)] font-semibold leading-[1.08] tracking-tight text-ink">
          Your home's data stays yours.
        </h2>
        <p className="mt-5 text-base leading-relaxed text-ink/65">
          Household records, documents and photos are private to your account and the family
          members you choose. Service providers cannot browse your assets or documents, and
          HomeAligns does not sell personal data or use advertising SDKs in the app.
        </p>
        <div className="mt-8 flex flex-wrap gap-4">
          <Link to="/privacy-policy" data-testid="trust-privacy-link" className="btn-secondary">
            Read the Privacy Policy
          </Link>
          <Link to="/account-deletion" data-testid="trust-data-rights-link" className="btn-secondary">
            Your data & deletion rights
          </Link>
        </div>
      </Reveal>
      <Reveal delay={1} className="grid gap-4 sm:grid-cols-2">
        {[
          ["Lock", "Private by default", "Files are stored privately and served only to signed-in users you allow."],
          ["Users", "You decide sharing", "Family members see your household only at the permission level you set."],
          ["ShieldCheck", "Providers see less", "Providers see only the summary needed for a request you confirm."],
          ["Trash2", "Delete anytime", "Export or erase your data from Profile → Privacy & data in the app."],
        ].map(([icon, t, d]) => (
          <div key={t} className="card-site p-6">
            <span className="icon-tile !h-10 !w-10 !rounded-xl">
              <Icon name={icon} className="h-4 w-4" />
            </span>
            <h3 className="mt-4 text-sm font-bold text-ink">{t}</h3>
            <p className="mt-1.5 text-xs leading-relaxed text-ink/60">{d}</p>
          </div>
        ))}
      </Reveal>
    </div>
  </section>
);

const FaqPreview = () => (
  <section className="py-24">
    <div className="container-site max-w-4xl">
      <SectionHead
        align="center"
        eyebrow="Questions, answered"
        title="Frequently asked questions."
      />
      <Reveal>
        <Accordion type="single" collapsible className="w-full" data-testid="home-faq-accordion">
          {FAQS.slice(0, 6).map((f, i) => (
            <AccordionItem key={f.q} value={`faq-${i}`} className="border-teal-deep/10">
              <AccordionTrigger
                data-testid={`home-faq-trigger-${i}`}
                className="text-left text-base font-bold text-ink hover:text-teal-deep hover:no-underline"
              >
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-sm leading-relaxed text-ink/65">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
      <Reveal className="mt-8 text-center">
        <Link to="/help" data-testid="faq-view-all-btn" className="btn-secondary">
          Visit the Help Centre <Icon name="ArrowRight" className="h-4 w-4" />
        </Link>
      </Reveal>
    </div>
  </section>
);

// Informational store badge. Renders as a real link only once a verified
// store URL is configured in STORE_LINKS — until then it is not clickable.
const StoreBadge = ({ store }) => {
  const isIos = store === "ios";
  const url = STORE_LINKS[store];
  const inner = (
    <>
      <Icon name={isIos ? "Apple" : "Play"} className="h-7 w-7 shrink-0" />
      <span className="text-left leading-tight">
        <span className="block text-[10px] font-semibold uppercase tracking-[0.14em] opacity-70">Coming Soon on</span>
        <span className="block text-base font-extrabold">{isIos ? "the App Store" : "Google Play"}</span>
      </span>
    </>
  );
  const cls = "flex items-center gap-3 rounded-2xl border border-teal-deep/15 bg-mist/60 px-5 py-3.5 text-ink";
  if (url) {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" data-testid={`store-badge-${store}`} className={`${cls} transition-transform duration-300 hover:-translate-y-0.5`}>
        {inner}
      </a>
    );
  }
  return (
    <div data-testid={`store-badge-${store}`} className={`${cls} cursor-default select-none`} aria-label={`Coming soon on ${isIos ? "the App Store" : "Google Play"}`}>
      {inner}
    </div>
  );
};

const LaunchSignup = () => {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | sending | done

  const submit = async (e) => {
    e.preventDefault();
    if (state === "sending") return;
    setState("sending");
    try {
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/launch-notify`, { email });
      setState("done");
      toast.success(res.data?.message || "You're on the list.");
    } catch (err) {
      setState("idle");
      const detail = err.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail[0]?.msg : "Could not add you right now — please try again.");
    }
  };

  return (
    <section className="pb-24 pt-2" id="mobile-app">
      <div className="container-site">
        <Reveal>
          <div className="grid items-center gap-10 rounded-[2.5rem] border border-teal-deep/10 bg-white p-8 shadow-sm md:p-12 lg:grid-cols-2">
            <div>
              <p className="eyebrow mb-3 flex items-center gap-2">
                <Icon name="Smartphone" className="h-4 w-4" /> HomeAligns Mobile App — Coming Soon
              </p>
              <h2 className="font-display text-[clamp(26px,3.5vw,40px)] font-semibold leading-[1.1] tracking-tight text-ink">
                Your whole home, soon in your pocket.
              </h2>
              <p className="mt-4 max-w-md text-sm leading-relaxed text-ink/60 md:text-base">
                Soon you'll manage household assets, documents, warranties, reminders and home
                services straight from your phone. HomeAligns works on the web today — the iOS and
                Android apps are on their way.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <StoreBadge store="ios" />
                <StoreBadge store="android" />
              </div>
              <p className="mt-5 text-sm text-ink/55">
                On the web already?{" "}
                <a href={APP_LINKS.getStarted} data-testid="launch-get-started-link" className="font-bold text-teal-deep underline underline-offset-4 hover:text-teal">Get Started</a>
                {" "}or{" "}
                <a href={APP_LINKS.login} data-testid="launch-login-link" className="font-bold text-teal-deep underline underline-offset-4 hover:text-teal">Log In</a>.
              </p>
            </div>
            <div>
              {state === "done" ? (
                <div className="flex items-center gap-4 rounded-2xl bg-teal-mist p-6" data-testid="launch-signup-success">
                  <span className="icon-tile !bg-white">
                    <Icon name="Check" className="h-5 w-5" />
                  </span>
                  <p className="text-sm font-bold text-teal-deep">
                    You're on the list — we'll email you when the app launches.
                  </p>
                </div>
              ) : (
                <form onSubmit={submit} className="flex flex-col gap-3 sm:flex-row" data-testid="launch-signup-form">
                  <label htmlFor="launch-email" className="sr-only">Email address</label>
                  <input
                    id="launch-email"
                    data-testid="launch-signup-email-input"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="input-site flex-1 !rounded-full !px-5 !py-3.5"
                  />
                  <button type="submit" data-testid="launch-signup-submit-btn" disabled={state === "sending"} className="btn-primary whitespace-nowrap disabled:opacity-60">
                    {state === "sending" ? "Adding…" : "Notify me at launch"}
                  </button>
                </form>
              )}
              <p className="mt-3 text-xs text-ink/45">
                One email when the apps launch. No marketing, no sharing — see our{" "}
                <Link to="/privacy-policy" className="font-semibold text-teal-deep underline underline-offset-2">Privacy Policy</Link>.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const FinalCta = () => (
  <section className="pb-24 pt-4">
    <div className="container-site">
      <Reveal>
        <div className="relative overflow-hidden rounded-[2.5rem] px-8 py-16 text-center text-white md:py-24" style={{ backgroundImage: "linear-gradient(125deg,#0A4D68,#148A9D)" }}>
          <div className="texture-grid absolute inset-0" aria-hidden="true" />
          <div className="relative">
            <h2 className="mx-auto max-w-3xl font-display text-[clamp(30px,4.5vw,54px)] font-semibold leading-[1.06] tracking-tight">
              Take Control of Your Home, the Smarter Way.
            </h2>
            <p className="mx-auto mt-5 max-w-xl text-base text-white/80">
              Create your free household account, or register your service business — it takes minutes.
            </p>
            <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
              <a
                href={APP_LINKS.getStarted}
                data-testid="final-get-started-btn"
                className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-sm font-bold text-teal-deep shadow-xl transition-transform duration-300 hover:-translate-y-0.5"
              >
                Get Started <Icon name="ArrowRight" className="h-4 w-4" />
              </a>
              <a
                href={APP_LINKS.registerVendor}
                data-testid="final-vendor-btn"
                className="inline-flex items-center gap-2 rounded-full border border-white/35 px-8 py-4 text-sm font-bold text-white transition-colors duration-300 hover:bg-white/10"
              >
                Join as a Service Professional
              </a>
            </div>
            <p className="mt-7 text-xs text-white/60">
              Mobile apps for iOS and Android are launching soon — HomeAligns works on the web today.
            </p>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Seo
        title="Everything Your Home Needs. Aligned in One Place."
        description="HomeAligns is the household management platform for India — manage assets, documents, expenses, bills and maintenance, and connect with trusted home-service professionals, all in one place."
        path="/"
      />
      <Hero />
      <Marquee />
      <Problems />
      <FeaturesGrid />
      <SmartReminders />
      <MoneySection />
      <ProductDemo />
      <HowItWorks />
      <IndiaSection />
      <ServicesPreview />
      <AISection />
      <SavingsGoals />
      <VendorSection />
      <PricingPreview />
      <TrustSection />
      <FaqPreview />
      <LaunchSignup />
      <FinalCta />
    </>
  );
}
